import { SortingState } from "@tanstack/react-table";
import TableContainer2 from "Common/Tables/ObjectFiltersTable/TableContainer2";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import usePermission from "CustomHooks/usePermission";
import { api } from "helpers/api_url";
import { app_modules } from "helpers/apps_modules_list";
import { useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { fetchDailyLogs } from "slices/Warehouse/ProjectWarehouse/DailyLog/thunk";

type PendingStockTransferProps = {
  outloading?: boolean;
  pageIndex: number;
  pageSize: number;
  sorting: SortingState;
  columnFilters: any[];
  filterValue: any[];
  search: string;
  searchInput: string;
  warehouseIdSearch: string;
  sortField: string;
  sortDirection: string;
  setSearchInput: React.Dispatch<React.SetStateAction<string>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setPageIndex: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
  setColumnFilters: React.Dispatch<React.SetStateAction<any[]>>;
  setFilterValue: React.Dispatch<React.SetStateAction<any[]>>;
  setWarehouseIdSearch: React.Dispatch<React.SetStateAction<string>>;
};

const DailyLog: React.FC<PendingStockTransferProps> = ({
  outloading,
  pageIndex,
  pageSize,
  sorting,
  columnFilters,
  filterValue,
  search,
  searchInput,
  warehouseIdSearch,
  sortField,
  sortDirection,
  setSearch,
  setSearchInput,
  setPageIndex,
  setPageSize,
  setSorting,
  setColumnFilters,
  setFilterValue,
  setWarehouseIdSearch,
}) => {
  document.title = "daily log";
  const dispatch = useDispatch<any>();
  const selectDailyLog = createSelector(
    (state: any) => state.DailyLog,
    (item) => ({
      list: item?.list || [],
      rowCount: item?.rowCount || 0,
      loading: item?.loading || false,
      error: item?.error || null,
    })
  );

  const { list, rowCount, loading, error } = useSelector(selectDailyLog);

  // Use custom hook to fetch and access permissions
  const { canView } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward
  );

  //*// Need to change this with fetching pending Stock Transfer data
  useEffect(() => {
    if (warehouseIdSearch) {
      dispatch(
        fetchDailyLogs({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? (sortDirection as "asc" | "desc") : "",
          search,
          filters: filterValue,
          warehouse_id: warehouseIdSearch,
        })
      );
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    search,
    filterValue,
    warehouseIdSearch,
  ]);
  //*//

  //*// Listing tanstack table columns
  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row, table }: { row: any; table: any }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          const rowIndex = row.index;
          return pageIndex * pageSize + rowIndex + 1;
        },
      },

      {
        header: "Item Name",
        accessorKey: "item_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/item_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.item_name || "-";
        },
      },
      {
        header: "Item Code",
        accessorKey: "item_code",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/item_code?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.item_code || "-";
        },
      },
      {
        header: "Category",
        accessorKey: "category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/category_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.category_name || "-";
        },
      },
      {
        header: "Sub Category",
        accessorKey: "sub_category_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/sub_category_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.sub_category_name || "-";
        },
      },
      {
        header: "Unit",
        accessorKey: "unit_name",
        enableSorting: true,
        enableColumnFilter: true,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/unit_name?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.unit_name || "-";
        },
      },

      {
        header: "Today In",
        accessorKey: "today_in",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/today_in?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.today_in ?? "-";
        },
      },
      {
        header: "Today Out",
        accessorKey: "today_out",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/today_out?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.today_out;
        },
      },

      {
        header: "Closing Stock",
        accessorKey: "closing_stock",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: `${api.warehouse.warehouse_overview.daily_log.dropdown}/closing_stock?warehouse_id=${warehouseIdSearch}`,
        cell: ({ row }: { row: any }) => {
          return row.original.closing_stock ?? "-";
        },
      },
    ],
    [dispatch, list, canView, warehouseIdSearch]
  );
  console.log("list : ", list);
  return (
    <Row id="invoiceList">
      <Col lg={12}>
        <Card>
          <Card.Body className="mt-3">
            <LoaderErrorWrapper loading={loading} error={error}>
              <TableContainer2
                columns={columns}
                data={list}
                totalRecords={rowCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                colValue={setFilterValue}
                sorting={sorting}
                onSortingChange={setSorting}
                isBordered={false}
                isPagination={true}
                isGlobalFilter={false}
                SearchPlaceholder="Search..."
                manualSorting={true}
              />
            </LoaderErrorWrapper>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default DailyLog;
